<template>
    <div class="article-blog-feedback">
        <p>
            Sadly, we have been made aware of a number of our new dancers being put off by other members telling them
            they are doing it wrong. I'm sure this was well meaning, but the giving of unsolicited feedback has
            unintended consequences. I wanted to discuss the topic of feedback, and make everyone aware that giving of
            feedback without being asked for it is now against our event terms and conditions.
        </p>
        <p>
            <strong>"Do not give feedback unless you are asked for it."</strong>
        </p>
        <content-subheading heading="Dance Etiquette" />
        <p>
            So many articles and videos discuss the finer points of dance technique, but very few address the finer
            points of dance etiquette - and if you're going to take your dancing to any level, that's just as important
            in your development as how to lead and follow pirouette turns. You won't get anywhere if nobody wants to
            dance with you anymore.
        </p>
        <p><em>Ok, so what am I talking about and what are the dangers?</em></p>
        <content-subheading heading="They Made Me Feel 'This' Small" />
        <p>
            In over a decade of teaching participatory partner dance, it's amazing how many times the hard work I put
            into welcoming new people, is undermined by well-meaning individuals giving feedback - particularly to
            beginners.
        </p>
        <p>
            The giver of feedback is thinking, I'm helping this person - they're making a mistake. &nbsp;I need to tell
            them how to do this step, I've been dancing for years - I know it all.
        </p>
        <p>
            The receiver? &nbsp;OMG, who does this person think they are? &nbsp;I can't believe they're telling me that
            I'm doing it wrong. &nbsp;I feel 'this' small right now. &nbsp;I can't wait to get out of here and never
            come back. &nbsp;They're not even doing it like the teacher.
        </p>
        <p>
            <strong
                >I can tell you from our comprehensive system of feedback and observation - both formal and informal -
                that people naturally avoid confrontation, so will smile, nod and then vote with their feet -
                by&nbsp;avoiding dancing with you, complaining or&nbsp;stepping out of the community altogether.</strong
            >
        </p>
        <p>
            Given the fact that they majority of people who step into classes are doing it to build confidence, make
            some new friends and have a laugh - an experience like the one above is a complete fail against all three.
        </p>
        <content-subheading heading="What qualifies as giving feedback?" />
        <p>In what circumstances do I think that giving feedback has risks? &nbsp;Well, in any situation actually:</p>
        <ul>
            <li>
                <p>
                    <strong>Well-meaning individuals in classes trying to teach other students.</strong>&nbsp;&nbsp;It
                    takes a great deal of self-evaluation and practice to become a good teacher - particularly in a
                    one-to-one setting. &nbsp;You may think you're explaining it correctly but take it from one who has
                    learnt through bitter experience, it takes lots of hard work and plenty of mistakes to develop into
                    an effective teacher. &nbsp;It takes two to tango, or swing for that matter, if you and your partner
                    are having difficulty with some material - direct a question to the teachers on duty.
                </p>
            </li>
            <li>
                <p>
                    <strong
                        >Similarly well-meaning individuals telling someone they're doing something wrong on the
                        freestyle dance floor.</strong
                    >&nbsp;&nbsp;This is an absolute no-no. &nbsp;And it comes in many guises. &nbsp;You might think
                    that telling someone how to do something is constructive, but the majority of people will just you
                    think you are telling them they are doing it wrong and it will damage their confidence.
                    &nbsp;Keeping your mouth shut is the safe bet.
                </p>
            </li>
            <li>
                <p>
                    <strong>Teachers pointing out an individual's mistake in front of a group of people.</strong
                    >&nbsp;&nbsp;This one is particularly destructive, most people don't like to be the centre of
                    attention, so calling out their mistakes in front of a group of their peers will be enough to leave
                    most people feeling mortified. &nbsp;It is strictly our policy to never do this, our teachers are
                    trained to look out for mistakes and then address feedback to the whole group. &nbsp;And yes, some
                    people will continue making mistakes because they don't listen and sometimes aren't aware.
                    &nbsp;That's just a fact of life and must be tolerated, this is not <em>The Royal Ballet</em>. Which
                    leads on to my next point...
                </p>
            </li>
            <li>
                <p>
                    <strong
                        >Teachers giving students one-to-one feedback either in the circle or a one-to-one
                        session.</strong
                    >&nbsp;&nbsp;While I'm teaching a class I drop into the circle and dance with the students during
                    the practice time, and I also give one-to-one sessions from time-to-time. &nbsp;On these occasions I
                    will very carefully give some limited feedback, using every ounce of experience I've developed in
                    the twelve years I've been teaching. &nbsp;But I'm very careful to ensure it is received in the
                    right spirit - and I would NEVER do this at a class where it isn't clear that I'm the teacher - or
                    for that matter, at another teacher's class. &nbsp;Or during a freestyle session, in these
                    circumstances I will try to communicate using my lead alone. &nbsp;Follows, you can do the same with
                    your following, by hijacking the lead if the dancer is out of time, leading you into something
                    uncomfortable, or if you want to mark the music in your own way.
                </p>
            </li>
            <li>
                <p>
                    <strong>Giving positive feedback.</strong>&nbsp;&nbsp;Admittedly, the risks involved with giving
                    positive feedback are not as numerous. &nbsp;But they still exist! &nbsp;Telling someone 'well
                    done', can be received as patronising and demeaning. &nbsp;So be careful, even with this!
                </p>
            </li>
        </ul>
        <content-subheading heading="A Simple Rule of Thumb" />
        <p>My advice on the matter of giving feedback&nbsp;can be simply summed up and stated as this:</p>
        <h2><strong>"Do not give feedback unless you are asked for it."</strong></h2>
        <p>
            As with all simple rules of thumb, there are some exceptions. &nbsp;But if you're going to bite the bullet
            and give some feedback, think long and hard first:
        </p>
        <ul>
            <li>
                <p>
                    <strong>If the person in question is in danger of causing harm or dancing inappropriately.</strong
                    >&nbsp;&nbsp;In this circumstance you are well within your rights to give that person some feedback.
                    &nbsp;Even better, politely excuse yourself and raise your concerns privately with an event
                    organiser. &nbsp;If your concerns aren't dealt with, find another event.
                </p>
            </li>
            <li>
                <p>
                    <strong>You are good friends with the person and know each other well.</strong>&nbsp;&nbsp;Now be
                    very careful with this one. &nbsp;At most dance events you will find that people are very friendly,
                    and will make a lot of new friends. &nbsp;But ask yourself, are you&nbsp;<em>good</em>&nbsp;friends?
                    &nbsp;Would you do things outside of dancing together, do you keep in touch with each other?
                    &nbsp;Do you have a relationship of trust in which you could talk to that person about anything?
                    &nbsp;If not, keep your feedback to yourself.
                </p>
            </li>
            <li>
                <p>
                    <strong>You are teaching a class.</strong>&nbsp;&nbsp;The danger of not giving feedback as a teacher
                    is that people will continue making some mistakes if they are not pointed out to them. &nbsp;This
                    will hamper their dance development as they internalise the mistakes. &nbsp;So it is your
                    responsibility to try and bring up those mistakes in a sensitive manner. &nbsp;But if you're new to
                    teaching, I'd advise keeping your feedback aimed at the group while you develop your style -
                    teaching one-to-one requires a very refined skill set. &nbsp;And keep your nose out, if it's not
                    your class and the individual doesn't know that you are a teacher.
                </p>
            </li>
        </ul>
        <content-subheading heading="In Summary" />
        <p>
            I accept that when people do give feedback it is well-meaning in the vast majority of cases, they're only
            trying to help.
        </p>
        <p>
            <em
                >In partner dance, awareness of yourself and your partner is one of the most difficult skills to
                develop</em
            >
            - much more difficult and subjective than the steps, timing or moves. It can take years to develop this, and
            in some cases it will never happen.
        </p>
        <p>
            So remember, the next time you think of making a comment to someone about their dancing - even a positive
            one:
        </p>
        <h2>"Do not give feedback unless you are asked for it."</h2>
    </div>
</template>

<script>
export default {
    name: 'ArticleBlogFeedback',
};
</script>
